/**
 * useMedicationSafety.ts — the ONE double-prescribing read (2026-08-07 Phase 2).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: key alerts.
 *
 * WHY this exists as a hook rather than staying inside ReviewView: the answer is
 * now needed in TWO places at once — the Key Alerts band inside the Review tab,
 * and the warning indicator on the Review TAB ITSELF, which has to be visible
 * before anyone opens that tab (CTO decision 3). The drawer therefore owns the
 * read and hands the SAME view down to the band, so the tab flag and the banner
 * can never disagree and the endpoint is called exactly once per patient.
 *
 * ReviewView keeps this hook for its standalone mount (no drawer above it), so
 * there is one implementation of the read, its retry semantics and its
 * fail-loud posture, not two.
 *
 * Deliberately NOT called on any patient surface: the patient lane is never
 * blocked and never told.
 */
import { useCallback, useEffect, useState } from 'react';
import { getMedicationSafety } from '../api/orders';
import { medicationSafetyFrom, type MedicationSafetyView } from './KeyAlerts';

/**
 * Run the check for one patient.
 *
 * `patientUserId` is null when the check does not apply (review-only mode with
 * no linked patient account, or an owner that has hoisted the read elsewhere) —
 * the view is then `not-applicable` and NO request is issued.
 *
 * `excludeOrderCaseId` is the case being reviewed, dropped server-side so the
 * order in front of the clinician never alerts against itself.
 */
export function useMedicationSafety(
  patientUserId: string | null,
  excludeOrderCaseId?: string,
): { view: MedicationSafetyView; retry: () => void } {
  const [view, setView] = useState<MedicationSafetyView>({ state: 'idle' });

  const load = useCallback(
    // `keepPrevious` is the RETRY path: the warning the clinician is looking at
    // must stay on screen while the re-check runs. A fresh load (a different
    // patient) never keeps it — an answer about one patient must never sit on
    // another patient's review, even for a moment.
    (keepPrevious = false) => {
      if (!patientUserId) {
        setView({ state: 'not-applicable' });
        return;
      }
      setView((current) => {
        if (!keepPrevious) return { state: 'loading', previous: null };
        if (current.state === 'ready') return { state: 'loading', previous: current.safety };
        if (current.state === 'loading') return current;
        return { state: 'loading', previous: null };
      });
      getMedicationSafety(patientUserId, excludeOrderCaseId)
        .then((data) => setView({ state: 'ready', safety: medicationSafetyFrom(data) }))
        // FAIL LOUD on the display: a failed check becomes its own explicit
        // state, never an empty screen that reads as "nothing found".
        .catch(() => setView({ state: 'ready', safety: { status: 'unavailable' } }));
    },
    [patientUserId, excludeOrderCaseId],
  );

  // Wrapped so a click event is never read as `keepPrevious`, and so a retry
  // always keeps the warning up.
  const retry = useCallback(() => load(true), [load]);

  useEffect(() => {
    // No argument: a fresh check for this patient, never carrying an answer over.
    load();
  }, [load]);

  return { view, retry };
}
